/**
* Admin API Service - Servizio unificato per operazioni admin
*
* Usa Supabase direttamente con le tabelle reali:
* - utenti (clienti)
* - ricette
* - faq
* - recensioni
* - press_articles
* - press_publications
* - riflessioni
*/

import { supabase } from '@/lib/supabase';
import { supabaseAuth } from '@/lib/supabase-auth';
import type { Riflessione } from '@/services/riflessioniService';

export type { Riflessione };

// =============================================================================
// TYPES
// =============================================================================

export interface Client {
  id_utente: number;
  nome: string | null;
  cognome: string | null;
  email: string | null;
  telefono: string | null;
  citta: string | null;
  provincia: string | null;
  regione: string | null;
  cap: string | null;
  indirizzo: string | null;
  codicefiscale: string | null;
  sesso: string | null;
  attivo: number | null;
  data_inserimento: string | null;
  ultimo_accesso: string | null;
  note: string | null;
  note_interne: string | null;
  allergie: string | null;
  peso_iniziale: number | null;
  obiettivo_peso: string | null;
  access_enabled: boolean | null;
  cliente_percorso: boolean | null;
}

export interface Recipe {
  id: number;
  name: string;
  title: string | null;
  description: string | null;
  price: string | null;
  category: string | null;
  macro_category: string | null;
  image_url: string | null;
  download_url: string | null;
  pdf_storage_path: string | null;
  prep_time: string | null;
  servings: string | null;
  difficulty: string | null;
  is_published: boolean | null;
  is_active: boolean | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface Review {
  id: number;
  nome: string;
  cognome: string;
  testo_recensione: string;
  benefici: string[] | null;
  data_recensione: string | null;
  utente_id: number | null;
  is_attiva: boolean | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface FAQ {
  id: number;
  domanda: string;
  risposta: string;
  categoria: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface PressArticle {
  id: number;
  title: string;
  publication_id: number | null;
  publication_name: string;
  publication_logo: string | null;
  date: string;
  link: string | null;
  excerpt: string | null;
  image: string | null;
  is_published: boolean | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface PressPublication {
  id: number;
  name: string;
  logo: string | null;
  website: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface SiteAlert {
  id: number;
  title: string | null;
  message: string;
  isActive: boolean;
  type: 'info' | 'warning' | 'success' | 'error';
  canClose: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface DashboardStats {
  totalClients: number;
  activeClients: number;
  totalRecipes: number;
  publishedRecipes: number;
  totalReviews: number;
  pendingReviews: number;
  totalFaqs: number;
  totalPressArticles: number;
}

export interface RecentActivity {
  type: 'client' | 'recipe' | 'review' | 'faq';
  action: string;
  name: string;
  date: string;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
}

export interface ClientFilters extends PaginationParams {
  search?: string;
  status?: 'active' | 'inactive' | 'all';
  type?: 'percorso' | 'acquirente' | 'prospect' | 'all';
  sortBy?: 'id_utente' | 'nome' | 'data_inserimento' | 'ultimo_accesso';
  sortOrder?: 'asc' | 'desc';
}

export interface RecipeFilters extends PaginationParams {
  search?: string;
  category?: string;
  isPublished?: boolean;
  sortBy?: 'name' | 'created_at' | 'price';
  sortOrder?: 'asc' | 'desc';
}

export interface ReviewFilters extends PaginationParams {
  isActive?: boolean;
  clienteId?: number;
  sortBy?: 'data_recensione' | 'created_at';
  sortOrder?: 'asc' | 'desc';
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// =============================================================================
// DASHBOARD
// =============================================================================

export async function getDashboardStats(): Promise<DashboardStats> {
  const [
    clientsResult,
    activeClientsResult,
    recipesResult,
    publishedRecipesResult,
    reviewsResult,
    pendingReviewsResult,
    faqsResult,
    pressResult
  ] = await Promise.all([
    // Solo clienti percorso (seguiti da Paolo, esclusi prospetti)
    supabase.from('utenti').select('*', { count: 'exact', head: true }).eq('cliente_percorso', true).neq('conosce', '0'),
    supabase.from('utenti').select('*', { count: 'exact', head: true }).eq('attivo', 1).eq('cliente_percorso', true).neq('conosce', '0'),
    supabase.from('ricette').select('*', { count: 'exact', head: true }),
    supabase.from('ricette').select('*', { count: 'exact', head: true }).eq('is_published', true),
    supabase.from('recensioni').select('*', { count: 'exact', head: true }),
    supabase.from('recensioni').select('*', { count: 'exact', head: true }).eq('is_attiva', false),
    supabase.from('faq').select('*', { count: 'exact', head: true }),
    supabase.from('press_articles').select('*', { count: 'exact', head: true }).eq('is_published', true)
  ]);

  return {
    totalClients: clientsResult.count || 0,
    activeClients: activeClientsResult.count || 0,
    totalRecipes: recipesResult.count || 0,
    publishedRecipes: publishedRecipesResult.count || 0,
    totalReviews: reviewsResult.count || 0,
    pendingReviews: pendingReviewsResult.count || 0,
    totalFaqs: faqsResult.count || 0,
    totalPressArticles: pressResult.count || 0
  };
}

export async function getRecentActivity(limit = 10): Promise<RecentActivity[]> {
  const [clientsRes, reviewsRes] = await Promise.all([
    supabase
      .from('utenti')
      .select('nome, cognome, data_inserimento')
      .order('data_inserimento', { ascending: false })
      .limit(5),
    supabase
      .from('recensioni')
      .select('nome, cognome, created_at, is_attiva')
      .order('created_at', { ascending: false })
      .limit(5)
  ]);

  const activities: RecentActivity[] = [];

  if (clientsRes.data) {
    clientsRes.data.forEach(c => {
      if (c.data_inserimento) {
        activities.push({
          type: 'client',
          action: 'Nuovo cliente registrato',
          name: `${c.nome || ''} ${c.cognome || ''}`.trim() || 'Anonimo',
          date: c.data_inserimento
        });
      }
    });
  }

  if (reviewsRes.data) {
    reviewsRes.data.forEach(r => {
      if (r.created_at) {
        activities.push({
          type: 'review',
          action: r.is_attiva ? 'Nuova recensione pubblicata' : 'Recensione in attesa',
          name: `${r.nome || ''} ${r.cognome || ''}`.trim() || 'Anonimo',
          date: r.created_at
        });
      }
    });
  }

  // Sort by date descending
  return activities
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, limit);
}

// =============================================================================
// CLIENTS (utenti)
// =============================================================================

export async function getClients(filters: ClientFilters = {}): Promise<PaginatedResponse<Client>> {
  const { page = 1, limit = 20, search, status, type, sortBy = 'id_utente', sortOrder = 'desc' } = filters;
  const offset = (page - 1) * limit;

  // Build query
  let query = supabase
    .from('utenti')
    .select('*', { count: 'exact' });

  // Apply search filter
  if (search) {
    query = query.or(`nome.ilike.%${search}%,cognome.ilike.%${search}%,email.ilike.%${search}%`);
  }

  // Apply status filter
  if (status === 'active') {
    query = query.eq('attivo', 1);
  } else if (status === 'inactive') {
    query = query.eq('attivo', 0);
  }

  // Apply type filter
  if (type === 'percorso') {
    query = query.eq('cliente_percorso', true).neq('conosce', '0');
  } else if (type === 'acquirente') {
    query = query.eq('cliente_percorso', false).neq('conosce', '0');
  } else if (type === 'prospect') {
    query = query.eq('conosce', '0');
  } else if (type === 'all') {
    query = query.neq('conosce', '0');
  }

  // Apply sorting
  query = query.order(sortBy, { ascending: sortOrder === 'asc' });

  // Apply pagination
  query = query.range(offset, offset + limit - 1);

  const { data, count, error } = await query;

  if (error) throw error;

  return {
    data: data || [],
    pagination: {
      page,
      limit,
      total: count || 0,
      totalPages: Math.ceil((count || 0) / limit)
    }
  };
}

export async function getClientById(id: number): Promise<Client | null> {
  const { data, error } = await supabase
    .from('utenti')
    .select('*')
    .eq('id_utente', id)
    .single();

  if (error) throw error;
  return data;
}

export async function updateClient(id: number, updates: Partial<Client>): Promise<Client> {
  const { data, error } = await supabase
    .from('utenti')
    .update(updates)
    .eq('id_utente', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function createClient(client: Partial<Client>): Promise<Client> {
  // Get max ID
  const { data: maxIdData } = await supabase
    .from('utenti')
    .select('id_utente')
    .order('id_utente', { ascending: false })
    .limit(1);

  const nextId = maxIdData && maxIdData.length > 0 ? maxIdData[0].id_utente + 1 : 1;

  const { data, error } = await supabase
    .from('utenti')
    .insert([{
      ...client,
      id_utente: nextId,
      data_inserimento: client.data_inserimento || new Date().toISOString(),
    }])
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteClient(id: number): Promise<void> {
  const { error } = await supabase
    .from('utenti')
    .delete()
    .eq('id_utente', id);

  if (error) throw error;
}

// =============================================================================
// RECIPES (ricette)
// =============================================================================

export async function getRecipes(filters: RecipeFilters = {}): Promise<PaginatedResponse<Recipe>> {
  const { page = 1, limit = 20, search, category, isPublished, sortBy = 'created_at', sortOrder = 'desc' } = filters;
  const offset = (page - 1) * limit;

  let query = supabase
    .from('ricette')
    .select('*', { count: 'exact' });

  if (search) {
    query = query.or(`name.ilike.%${search}%,title.ilike.%${search}%,description.ilike.%${search}%`);
  }

  if (category) {
    query = query.eq('category', category);
  }

  if (isPublished !== undefined) {
    query = query.eq('is_published', isPublished);
  }

  query = query.order(sortBy, { ascending: sortOrder === 'asc' });
  query = query.range(offset, offset + limit - 1);

  const { data, count, error } = await query;

  if (error) throw error;

  return {
    data: data || [],
    pagination: {
      page,
      limit,
      total: count || 0,
      totalPages: Math.ceil((count || 0) / limit)
    }
  };
}

export async function getRecipeById(id: number): Promise<Recipe | null> {
  const { data, error } = await supabase
    .from('ricette')
    .select('*')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data;
}

export async function updateRecipe(id: number, updates: Partial<Recipe>): Promise<Recipe> {
  const { data, error } = await supabase
    .from('ricette')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function createRecipe(recipe: Omit<Recipe, 'id' | 'created_at' | 'updated_at'>): Promise<Recipe> {
  const { data, error } = await supabase
    .from('ricette')
    .insert(recipe)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteRecipe(id: number): Promise<void> {
  const { error } = await supabase
    .from('ricette')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// =============================================================================
// REVIEWS (recensioni)
// =============================================================================

export async function getReviews(filters: ReviewFilters = {}): Promise<PaginatedResponse<Review>> {
  const { page = 1, limit = 20, isActive, sortBy = 'created_at', sortOrder = 'desc' } = filters;
  const offset = (page - 1) * limit;

  let query = supabase
    .from('recensioni')
    .select('*', { count: 'exact' });

  if (isActive !== undefined) {
    query = query.eq('is_attiva', isActive);
  }

  if (filters.clienteId) {
    query = query.eq('utente_id', filters.clienteId);
  }

  query = query.order(sortBy, { ascending: sortOrder === 'asc' });
  query = query.range(offset, offset + limit - 1);

  const { data, count, error } = await query;

  if (error) throw error;

  return {
    data: data || [],
    pagination: {
      page,
      limit,
      total: count || 0,
      totalPages: Math.ceil((count || 0) / limit)
    }
  };
}

export async function getReviewById(id: number): Promise<Review | null> {
  const { data, error } = await supabase
    .from('recensioni')
    .select('*')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data;
}

export async function createReview(review: Omit<Review, 'id' | 'created_at' | 'updated_at'>): Promise<Review> {
  const { data, error } = await supabase
    .from('recensioni')
    .insert(review)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateReview(id: number, updates: Partial<Review>): Promise<Review> {
  const { data, error } = await supabase
    .from('recensioni')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function approveReview(id: number): Promise<Review> {
  return updateReview(id, { is_attiva: true });
}

export async function rejectReview(id: number): Promise<Review> {
  return updateReview(id, { is_attiva: false });
}

export async function deleteReview(id: number): Promise<void> {
  const { error } = await supabase
    .from('recensioni')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// =============================================================================
// FAQs
// =============================================================================

export async function getFaqs(filters: PaginationParams = {}): Promise<PaginatedResponse<FAQ>> {
  const { page = 1, limit = 200 } = filters;
  const offset = (page - 1) * limit;

  const { data, count, error } = await supabase
    .from('faq')
    .select('*', { count: 'exact' })
    .order('categoria', { ascending: true, nullsFirst: false })
    .range(offset, offset + limit - 1);

  if (error) throw error;

  const sorted = (data || []).sort((a, b) => {
    const numA = parseInt(a.domanda.split('-')[0]) || 0;
    const numB = parseInt(b.domanda.split('-')[0]) || 0;
    return numA - numB;
  });

  return {
    data: sorted,
    pagination: {
      page,
      limit,
      total: count || 0,
      totalPages: Math.ceil((count || 0) / limit)
    }
  };
}

export async function getFaqById(id: number): Promise<FAQ | null> {
  const { data, error } = await supabase
    .from('faq')
    .select('*')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data;
}

export async function createFaq(faq: Omit<FAQ, 'id' | 'created_at' | 'updated_at'>): Promise<FAQ> {
  const { data, error } = await supabase
    .from('faq')
    .insert(faq)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateFaq(id: number, updates: Partial<FAQ>): Promise<FAQ> {
  const { data, error } = await supabase
    .from('faq')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteFaq(id: number): Promise<void> {
  const { error } = await supabase
    .from('faq')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// =============================================================================
// PRESS
// =============================================================================

export async function getPressArticles(filters: PaginationParams = {}): Promise<PaginatedResponse<PressArticle>> {
  const { page = 1, limit = 20 } = filters;
  const offset = (page - 1) * limit;

  const { data, count, error } = await supabase
    .from('press_articles')
    .select('*', { count: 'exact' })
    .order('date', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) throw error;

  return {
    data: data || [],
    pagination: {
      page,
      limit,
      total: count || 0,
      totalPages: Math.ceil((count || 0) / limit)
    }
  };
}

export async function getPressPublications(): Promise<PressPublication[]> {
  const { data, error } = await supabase
    .from('press_publications')
    .select('*')
    .order('name', { ascending: true });

  if (error) throw error;
  return data || [];
}

// =============================================================================
// RIFLESSIONI (sezione /riflessioni del sito)
// =============================================================================

export type RiflessioneInput = Pick<
  Riflessione,
  'slug' | 'title' | 'excerpt' | 'content' | 'cover_image' | 'is_published' | 'published_at'
>;

// Le riflessioni passano dal server (routes-admin-riflessioni.ts) e non da Supabase
// diretto: il gestionale non usa Supabase Auth, quindi dal browser le richieste sono
// anonime e la RLS della tabella concede agli anonimi solo la lettura dei pubblicati.
async function riflessioniRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api/admin/riflessioni${path}`, {
    ...init,
    headers: await getAdminAuthHeaders(),
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(error?.message || 'Operazione non riuscita');
  }
  return response.status === 204 ? (undefined as T) : response.json();
}

/** Tutte le riflessioni, bozze comprese */
export function getRiflessioni(): Promise<Riflessione[]> {
  return riflessioniRequest('');
}

/** Una riflessione dal suo slug, anche se è una bozza (anteprima sul sito) */
export function getRiflessioneForPreview(slug: string): Promise<Riflessione> {
  return riflessioniRequest(`/by-slug/${encodeURIComponent(slug)}`);
}

export function createRiflessione(riflessione: RiflessioneInput): Promise<Riflessione> {
  return riflessioniRequest('', { method: 'POST', body: JSON.stringify(riflessione) });
}

export function updateRiflessione(id: number, updates: Partial<RiflessioneInput>): Promise<Riflessione> {
  return riflessioniRequest(`/${id}`, { method: 'PATCH', body: JSON.stringify(updates) });
}

export function deleteRiflessione(id: number): Promise<void> {
  return riflessioniRequest(`/${id}`, { method: 'DELETE' });
}

// =============================================================================
// SITE ALERTS (Express API)
// =============================================================================

/**
 * Helper per ottenere gli header di autenticazione admin
 * Supporta sia session cookie che X-Admin-Id header (per Supabase Admin Auth)
 */
async function getAdminAuthHeaders(): Promise<HeadersInit> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // Ottieni l'ID admin dalla sessione supabaseAuth (localStorage)
  const adminSession = supabaseAuth.getSession();
  if (adminSession?.id) {
    headers['X-Admin-Id'] = adminSession.id;
  }

  return headers;
}

export async function getSiteAlerts(): Promise<SiteAlert[]> {
  const headers = await getAdminAuthHeaders();
  const response = await fetch('/api/admin/alerts', {
    headers,
    credentials: 'include'
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch alerts' }));
    throw new Error(error.message || 'Failed to fetch alerts');
  }
  return response.json();
}

export async function createSiteAlert(alert: Partial<SiteAlert>): Promise<SiteAlert> {
  const headers = await getAdminAuthHeaders();
  const response = await fetch('/api/admin/alerts', {
    method: 'POST',
    headers,
    body: JSON.stringify(alert),
    credentials: 'include'
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to create alert' }));
    throw new Error(error.message || 'Failed to create alert');
  }
  return response.json();
}

export async function updateSiteAlert(id: number, updates: Partial<SiteAlert>): Promise<SiteAlert> {
  const headers = await getAdminAuthHeaders();
  const response = await fetch(`/api/admin/alerts/${id}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(updates),
    credentials: 'include'
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to update alert' }));
    throw new Error(error.message || 'Failed to update alert');
  }
  return response.json();
}

export async function deleteSiteAlert(id: number): Promise<void> {
  const headers = await getAdminAuthHeaders();
  const response = await fetch(`/api/admin/alerts/${id}`, {
    method: 'DELETE',
    headers,
    credentials: 'include'
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to delete alert' }));
    throw new Error(error.message || 'Failed to delete alert');
  }
}

export async function getActiveSiteAlert(): Promise<SiteAlert | null> {
  const response = await fetch('/api/alerts/active');
  if (!response.ok) return null;
  return response.json();
}

// =============================================================================
// ADMIN API OBJECT (for backwards compatibility with existing code)
// =============================================================================

/**
 * Restituisce gli ID dei clienti che hanno acquistato SOLO ricettari
 * (nessuna consulenza pagata e nessun prodotto). Questi clienti non fanno
 * parte del "percorso" e possono essere nascosti dal CRM.
 */
export async function getRicettarioOnlyClientIds(): Promise<Set<number>> {
  // Query parallele per ridurre latenza (1 round trip invece di 3)
  const [{ data: consultations }, { data: productPurchases }, { data: recipePurchases }] = await Promise.all([
    supabase.from('consultation_bookings').select('email').eq('payment_status', 'paid'),
    supabase.from('product_purchases').select('customer_email'),
    supabase.from('recipe_purchases').select('customer_email'),
  ]);

  const percorsoEmails = new Set(
    (consultations || []).map(c => (c.email || '').toLowerCase()).filter(Boolean)
  );

  const productEmails = new Set(
    (productPurchases || []).map(p => (p.customer_email || '').toLowerCase()).filter(Boolean)
  );

  const recipeEmails = new Set(
    (recipePurchases || []).map(r => (r.customer_email || '').toLowerCase()).filter(Boolean)
  );

  // 4. Email che hanno SOLO ricette (no consulenze, no prodotti)
  const ricettarioOnlyEmails = new Set<string>();
  for (const email of recipeEmails) {
    if (!percorsoEmails.has(email) && !productEmails.has(email)) {
      ricettarioOnlyEmails.add(email);
    }
  }

  // 5. Trova gli id_utente corrispondenti (match case-insensitive)
  if (ricettarioOnlyEmails.size === 0) return new Set();

  const { data: clients } = await supabase
    .from('utenti')
    .select('id_utente, email');

  return new Set(
    (clients || [])
      .filter(c => ricettarioOnlyEmails.has((c.email || '').toLowerCase()))
      .map(c => c.id_utente)
  );
}

export const adminApi = {
  // Dashboard
  getDashboardStats,
  getRecentActivity,

  // Clients
  getClients,
  getClientById,
  getRicettarioOnlyClientIds,
  createClient,
  updateClient,
  deleteClient,

  // Recipes
  getRecipes,
  getRecipeById,
  createRecipe,
  updateRecipe,
  deleteRecipe,

  // Reviews
  getReviews,
  getReviewById,
  createReview,
  updateReview,
  approveReview,
  rejectReview,
  deleteReview,

  // FAQs
  getFaqs,
  getFaqById,
  createFaq,
  updateFaq,
  deleteFaq,

  // Press
  getPressArticles,
  getPressPublications,

  // Riflessioni
  getRiflessioni,
  createRiflessione,
  updateRiflessione,
  deleteRiflessione,

  // Site Alerts
  getSiteAlerts,
  createSiteAlert,
  updateSiteAlert,
  deleteSiteAlert,
  getActiveSiteAlert
};

export default adminApi;
